import { ConfigurableProduct } from '@magentoTypes'
import { useTranslation } from 'next-i18next'
import { Accordion } from 'elements/Accordion/Accordion'
import ReactHtmlParser from 'react-html-parser'
import clsx from 'clsx'
import DetailsTable from './DetailsTable'
import Wolle from './Icons/Wolle'
import Cotton from './Icons/Cotton'
import Sustainability from './Icons/Sustainability'
import Handmade from './Icons/Handmade'
import Washable from './Icons/Washable'
import {
  checkHasSustainability,
  checkIsCotton,
  checkIsHandmade,
  checkIsWashable,
  checkIsWolle,
} from '~/lib/getProductFeature'
import { mapProductAttributesDetails } from '../lib/mapProductAttributesDetails'

type Props = {
  product: ConfigurableProduct
  className?: string
}
export const Description = ({ product, className }: Props) => {
  const { t } = useTranslation()
  if (!product) return null

  const detailsArray = mapProductAttributesDetails(product)
  const hasSustainability = checkHasSustainability(product?.benuta_test_seal as string)
  const isHandmade = checkIsHandmade(product?.benuta_production_type as string)
  const isCotton = checkIsCotton(product?.benuta_pile_material as number)
  const isWolle = checkIsWolle(product?.benuta_pile_material as number)
  const isWashable = checkIsWashable(product?.benuta_waschbar as number)

  const shouldDisplayLabels = isWolle || isCotton || hasSustainability || isHandmade || isWashable

  const itemList = [
    {
      itemHeading: t('Product Description'),
      richText: <div className="py-4">{ReactHtmlParser(product?.description?.html as string)}</div>,
    },
    {
      itemHeading: t('Product Details'),
      richText: (
        <div className="pb-4">
          <div className="mt-2 flex gap-x-3.5">
            {isWolle && (
              <div className="flex w-fit items-center	justify-center bg-[#EFEEE5] px-4 text-[#141414]">
                <Wolle />
                <span className="ml-1 text-[9px]">{t('wool')}</span>
              </div>
            )}
            {isCotton && !isWolle && (
              <div className="flex w-fit items-center	justify-center bg-[#EFEEE5] px-4 text-[#141414]">
                <Cotton />
                <span className="ml-1 text-[9px]">{t('cotton')}</span>
              </div>
            )}
            {hasSustainability && (
              <div className="flex w-fit items-center	justify-center bg-[#BCD0BC] px-4 text-[#141414]">
                <Sustainability />
                <span className="ml-1 text-[9px]">{t('certified')}</span>
              </div>
            )}
            {isHandmade && (
              <div className="flex w-fit items-center	justify-center bg-[#E3E1E4] px-4 text-[#141414]">
                <Handmade />
                <span className="ml-1 text-[9px]">{t('handmade')}</span>
              </div>
            )}
            {isWashable && !isHandmade && !hasSustainability && (
              <div className="flex w-fit items-center	justify-center bg-[#BBDEDD] px-4 text-[#141414]">
                <Washable />
                <span className="ml-1 text-[9px]">{t('washable')}</span>
              </div>
            )}
          </div>
          <div className="mt-10">
            <DetailsTable detailArray={detailsArray} />
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className={clsx(className)}>
      <div id="product-description" className="invisible absolute top-[-91px] h-[91px]"></div>
      <div className="hidden lg:block">
        <div className="grid grid-cols-2 gap-[40px_80px] bg-grey-light p-20">
          <div>
            <p className="mb-6 text-h3-mobile font-medium">{t('Product Description')}</p>
            <div className="text-15-22-sg font-extralight">
              {ReactHtmlParser(product?.description?.html as string)}
            </div>
          </div>
          <div>
            <p className="mb-6 text-h3-mobile font-medium">{t('Product Details')}</p>
            {shouldDisplayLabels && (
              <div className="mb-10 flex gap-x-3.5">
                {isWolle && (
                  <div className="flex w-fit items-center justify-center bg-[#EFEEE5] px-4 text-[#141414]">
                    <Wolle />
                    <span className="ml-1 text-[9px]">{t('wool')}</span>
                  </div>
                )}
                {isCotton && !isWolle && (
                  <div className="flex w-fit items-center justify-center bg-[#EFEEE5] px-4 text-[#141414]">
                    <Cotton />
                    <span className="ml-1 text-[9px]">{t('cotton')}</span>
                  </div>
                )}
                {hasSustainability && (
                  <div className="flex w-fit items-center justify-center bg-[#BCD0BC] px-4 text-[#141414]">
                    <Sustainability />
                    <span className="ml-1 text-[9px]">{t('certified')}</span>
                  </div>
                )}
                {isHandmade && (
                  <div className="flex w-fit items-center justify-center bg-[#E3E1E4] px-4 text-[#141414]">
                    <Handmade />
                    <span className="ml-1 text-[9px]">{t('handmade')}</span>
                  </div>
                )}
                {isWashable && !isHandmade && !hasSustainability && (
                  <div className="flex w-fit items-center justify-center bg-[#BBDEDD] px-4 text-[#141414]">
                    <Washable />
                    <span className="ml-1 text-[9px]">{t('washable')}</span>
                  </div>
                )}
              </div>
            )}
            <DetailsTable detailArray={detailsArray} />
          </div>
        </div>
      </div>
      <div className="lg:hidden">
        <div className="p-4">
          <Accordion
            itemList={itemList}
            classNameAccordionItem="border-b border-[#E6E6E6]"
            onlyApplyForLastElement
          />
        </div>
      </div>
    </div>
  )
}
